// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrencies } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import React, { FC } from "react"; // Starter Custom Code
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import SelectInline, { SelectInlineStyles } from "@store/Components/Toolkit/SelectInline";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import React, { FC, useMemo } from "react";
/* Starter Custom Code - End */

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    currencies: getCurrencies(state),
    currentCurrencyId: state.context.session.currency?.id,
    currentCurrencySymbol: state.context.session.currency?.currencySymbol,
});

const mapDispatchToProps = {
    setCurrency,
};

export interface CurrencyMenuStyles {
    currencyWrapper?: InjectableCss;
    currencySelect?: SelectPresentationProps;
    currencySymbol?: TypographyPresentationProps;
    // Starter Custom Code
    selectInline?: SelectInlineStyles;
}

export const currencyMenuStyles: CurrencyMenuStyles = {
    currencyWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;

            &:hover {
                color: ${getColor("menu.secondaryNavigation.hover")};
                text-decoration: underline;
                transition: ${transitionHoverStyles()};
            }
        `,
    },
    selectInline: {
        select: {
            cssOverrides: {
                inputSelect: css`
                    font-weight: inherit;
                    line-height: inherit;
                    cursor: pointer;

                    &:hover {
                        text-decoration: underline;
                    }
                `,
            },
        },
    },
    currencySymbol: {
        color: "inherit",
        css: css`
            position: absolute;
            font-size: ${pxToRem(18)};
            font-weight: ${x => x.theme.design.typography.weight.base};
            line-height: 0;
            cursor: default;
            pointer-events: none;
            user-select: none;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const CurrencyMenu: FC<Props> = ({
    currencies,
    currentCurrencyId,
    currentCurrencySymbol,
    fields,
    setCurrency,
}) => {
    if (!currencies || currencies.length <= 1) {
        return null;
    }

    const styles = useMergeStyles("currencyMenu", currencyMenuStyles);

    const menuId = "currencyMenu";

    /* Starter Custom Code - Start */
    const selectInlineStyles = useMemo(() => {
        if (fields.showIcon) {
            return mergeProps<SelectInlineStyles>(styles.selectInline, {
                select: {
                    cssOverrides: {
                        inputSelect: css`
                            ${({ theme }) => {
                                const iconSpacing = theme.design.menu.secondaryNavigation.iconSpacing;
                                return `padding-left: calc(${pxToRem(10)} + ${iconSpacing}) !important;`;
                            }}
                        `,
                    },
                },
            });
        }

        return styles.selectInline;
    }, [styles, fields.showIcon]);
    /* Starter Custom Code - End */

    return (
        <StyledWrapper {...styles.currencyWrapper}>
            {fields.showIcon ? <Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography> : null}
            <VisuallyHidden as="label" id={`${menuId}-label`} htmlFor={menuId}>
                {translate("Currency")}
            </VisuallyHidden>
            <SelectInline
                extendedStyles={selectInlineStyles} // Starter Custom Code
                uid={menuId}
                data-test-selector={menuId}
                onChange={event => setCurrency({ currencyId: event.currentTarget.value })}
                value={currentCurrencyId}
                autoComplete="off"
                hasLabel
            >
                {currencies.map(c => (
                    <option value={c.id} key={c.id} data-test-selector={c.currencyCode}>
                        {c.currencyCode}
                    </option>
                ))}
            </SelectInline>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrencyMenu),
    definition: {
        displayName: "Currency Menu",
        icon: "money-bill",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
